import { DashboardShell } from '@/components/dashboard/dashboard-shell'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useTRPC } from '@/trpc/react'
import { ALLOWED_BUSINESS_IDS_FOR_SEED } from '@repo/shared_utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Database, FileText, Loader2, Terminal, X } from 'lucide-react'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

export const Route = createFileRoute('/dashboard/admin/super_admin/seed-ie298')({
  component: RouteComponent,
})

type LogEntry = {
  id: string
  timestamp: Date
  operation: string
  status: 'success' | 'error' | 'info'
  message: string
  details?: string
}

function RouteComponent() {
  const api = useTRPC()
  const [employeeDataFile, setEmployeeDataFile] = useState<File | null>(null)
  const [clientDataFile, setClientDataFile] = useState<File | null>(null)
  const [selectedBusinessId, setSelectedBusinessId] = useState<string>('')
  const [nameType, setNameType] = useState<'fictional' | 'corporate'>('fictional')
  const [holidayYear, setHolidayYear] = useState<number>(new Date().getFullYear())
  const [logs, setLogs] = useState<LogEntry[]>([])
  const currentSeedingYearRef = useRef<number>(new Date().getFullYear())

  // Fetch all organizations
  const { data: organizations, isPending: isLoadingOrganizations } = useQuery(
    api.superAdmin.getOrganizations.queryOptions(),
  )

  function isBusinessAllowed(businessId: string): boolean {
    return ALLOWED_BUSINESS_IDS_FOR_SEED.includes(
      businessId as (typeof ALLOWED_BUSINESS_IDS_FOR_SEED)[number],
    )
  }

  function addLog(
    operation: string,
    status: 'success' | 'error' | 'info',
    message: string,
    details?: string,
  ): void {
    const logEntry: LogEntry = {
      id: `${Date.now()}-${Math.random()}`,
      timestamp: new Date(),
      operation,
      status,
      message,
      details,
    }
    setLogs((prev) => [...prev, logEntry])
  }

  function clearLogs(): void {
    setLogs([])
  }

  const seedCalendarCategories = useMutation({
    ...api.superAdmin.seedIe298CalendarCategories.mutationOptions({
      onSuccess: (result) => {
        // Add all logs from server
        result.logs.forEach((log) => {
          addLog('Calendar Categories', 'info', log.message)
        })
        const message = `Calendar categories seeded successfully. Regular Holiday ID: ${result.data.regularHolidayId}, Special Holiday ID: ${result.data.specialHolidayId}, Regional Holiday ID: ${result.data.regionalHolidayId}`
        addLog('Calendar Categories', 'success', message, JSON.stringify(result.data, null, 2))
        toast.success('Calendar categories seeded successfully')
      },
      onError: (error) => {
        const errorDetails = error.message || 'Unknown error occurred'
        const errorStack =
          'cause' in error &&
          typeof error.cause === 'object' &&
          error.cause &&
          'stack' in error.cause
            ? String(error.cause.stack)
            : undefined
        addLog(
          'Calendar Categories',
          'error',
          `Failed to seed calendar categories: ${errorDetails}`,
          errorStack,
        )
        toast.error(`Failed to seed calendar categories: ${error.message}`)
      },
    }),
  })

  const seedHolidays = useMutation({
    ...api.superAdmin.seedIe298Holidays.mutationOptions({
      onSuccess: (result) => {
        const year = currentSeedingYearRef.current
        // Add all logs from server
        result.logs.forEach((log) => {
          addLog(`PH Holidays ${year}`, 'info', log.message)
        })
        addLog(
          `PH Holidays ${year}`,
          'success',
          `All Philippine holidays for ${year} seeded successfully`,
        )
        toast.success('Holidays seeded successfully')
      },
      onError: (error) => {
        const year = currentSeedingYearRef.current
        const errorDetails = error.message || 'Unknown error occurred'
        const errorStack =
          'cause' in error &&
          typeof error.cause === 'object' &&
          error.cause &&
          'stack' in error.cause
            ? String(error.cause.stack)
            : undefined
        addLog(
          `PH Holidays ${year}`,
          'error',
          `Failed to seed holidays: ${errorDetails}`,
          errorStack,
        )
        toast.error(`Failed to seed holidays: ${error.message}`)
      },
    }),
  })

  const seedClients = useMutation({
    ...api.superAdmin.seedIe298Clients.mutationOptions({
      onSuccess: (result) => {
        // Add all logs from server
        result.logs.forEach((log) => {
          addLog('Clients', 'info', log.message)
        })
        const message = `Clients seeded successfully. Created ${result.data.length} client(s)`
        addLog('Clients', 'success', message, JSON.stringify(result.data, null, 2))
        toast.success('Clients seeded successfully')
      },
      onError: (error) => {
        const errorDetails = error.message || 'Unknown error occurred'
        const errorStack =
          'cause' in error &&
          typeof error.cause === 'object' &&
          error.cause &&
          'stack' in error.cause
            ? String(error.cause.stack)
            : undefined
        addLog('Clients', 'error', `Failed to seed clients: ${errorDetails}`, errorStack)
        toast.error(`Failed to seed clients: ${error.message}`)
      },
    }),
  })

  const seedUsersAndEmployees = useMutation({
    ...api.superAdmin.seedIe298UsersAndEmployees.mutationOptions({
      onSuccess: (result) => {
        // Add all logs from server
        result.logs.forEach((log) => {
          addLog('Users & Employees', 'info', log.message)
        })
        addLog(
          'Users & Employees',
          'success',
          'Users and employees seeded successfully. Created 25 users with matching employee profiles.',
        )
        toast.success('Users and employees seeded successfully')
      },
      onError: (error) => {
        const errorDetails = error.message || 'Unknown error occurred'
        const errorStack =
          'cause' in error &&
          typeof error.cause === 'object' &&
          error.cause &&
          'stack' in error.cause
            ? String(error.cause.stack)
            : undefined
        addLog(
          'Users & Employees',
          'error',
          `Failed to seed users and employees: ${errorDetails}`,
          errorStack,
        )
        toast.error(`Failed to seed users and employees: ${error.message}`)
      },
    }),
  })

  const seedClientSettings = useMutation({
    ...api.superAdmin.seedIe298ClientSettings.mutationOptions({
      onSuccess: (result) => {
        // Add all logs from server
        result.logs.forEach((log) => {
          addLog('Client Settings', 'info', log.message)
        })
        addLog('Client Settings', 'success', 'Client settings seeded successfully for all clients')
        toast.success('Client settings seeded successfully')
      },
      onError: (error) => {
        const errorDetails = error.message || 'Unknown error occurred'
        const errorStack =
          'cause' in error &&
          typeof error.cause === 'object' &&
          error.cause &&
          'stack' in error.cause
            ? String(error.cause.stack)
            : undefined
        addLog(
          'Client Settings',
          'error',
          `Failed to seed client settings: ${errorDetails}`,
          errorStack,
        )
        toast.error(`Failed to seed client settings: ${error.message}`)
      },
    }),
  })

  const seedEmployeeSettings = useMutation({
    ...api.superAdmin.seedIe298EmployeeSettings.mutationOptions({
      onSuccess: (result) => {
        // Add all logs from server
        result.logs.forEach((log) => {
          addLog('Employee Settings', 'info', log.message)
        })
        addLog(
          'Employee Settings',
          'success',
          'Employee settings seeded successfully for all employees',
        )
        toast.success('Employee settings seeded successfully')
      },
      onError: (error) => {
        const errorDetails = error.message || 'Unknown error occurred'
        const errorStack =
          'cause' in error &&
          typeof error.cause === 'object' &&
          error.cause &&
          'stack' in error.cause
            ? String(error.cause.stack)
            : undefined
        addLog(
          'Employee Settings',
          'error',
          `Failed to seed employee settings: ${errorDetails}`,
          errorStack,
        )
        toast.error(`Failed to seed employee settings: ${error.message}`)
      },
    }),
  })

  const seedGovernmentContributionBrackets = useMutation({
    ...api.superAdmin.seedIe298GovernmentContributionBrackets.mutationOptions({
      onSuccess: (result) => {
        // Add all logs from server
        result.logs.forEach((log) => {
          addLog('Government Contribution Brackets', 'info', log.message)
        })
        addLog(
          'Government Contribution Brackets',
          'success',
          'Government contribution brackets (SSS, HDMF, PhilHealth, Tax) seeded successfully',
        )
        toast.success('Government contribution brackets seeded successfully')
      },
      onError: (error) => {
        const errorDetails = error.message || 'Unknown error occurred'
        const errorStack =
          'cause' in error &&
          typeof error.cause === 'object' &&
          error.cause &&
          'stack' in error.cause
            ? String(error.cause.stack)
            : undefined
        addLog(
          'Government Contribution Brackets',
          'error',
          `Failed to seed government contribution brackets: ${errorDetails}`,
          errorStack,
        )
        toast.error(`Failed to seed government contribution brackets: ${error.message}`)
      },
    }),
  })

  // const seedGovernmentContributions = useMutation({
  //   ...api.superAdmin.seedIe298GovernmentContributions.mutationOptions({
  //     onSuccess: (result) => {
  //       // Add all logs from server
  //       result.logs.forEach((log) => {
  //         addLog('Government Contributions', 'info', log.message)
  //       })
  //       addLog(
  //         'Government Contributions',
  //         'success',
  //         'Government contributions (SSS, PhilHealth, HDMF) seeded successfully for all employees',
  //       )
  //       toast.success('Government contributions seeded successfully')
  //     },
  //     onError: (error) => {
  //       const errorDetails = error.message || 'Unknown error occurred'
  //       const errorStack =
  //         'cause' in error &&
  //         typeof error.cause === 'object' &&
  //         error.cause &&
  //         'stack' in error.cause
  //           ? String(error.cause.stack)
  //           : undefined
  //       addLog(
  //         'Government Contributions',
  //         'error',
  //         `Failed to seed government contributions: ${errorDetails}`,
  //         errorStack,
  //       )
  //       toast.error(`Failed to seed government contributions: ${error.message}`)
  //     },
  //   }),
  // })

  const seedTimesheets = useMutation({
    ...api.superAdmin.seedIe298Timesheets.mutationOptions({
      onSuccess: (result) => {
        // Add all logs from server
        result.logs.forEach((log) => {
          addLog('Timesheets', 'info', log.message)
        })
        addLog(
          'Timesheets',
          'success',
          'Timesheets seeded successfully from March 1 to November 22, 2025',
        )
        toast.success('Timesheets seeded successfully')
      },
      onError: (error) => {
        const errorDetails = error.message || 'Unknown error occurred'
        const errorStack =
          'cause' in error &&
          typeof error.cause === 'object' &&
          error.cause &&
          'stack' in error.cause
            ? String(error.cause.stack)
            : undefined
        addLog('Timesheets', 'error', `Failed to seed timesheets: ${errorDetails}`, errorStack)
        toast.error(`Failed to seed timesheets: ${error.message}`)
      },
    }),
  })

  const validateSeededData = useMutation({
    ...api.superAdmin.validateIe298SeededData.mutationOptions({
      onSuccess: (result) => {
        // Add all logs from server
        result.logs.forEach((log) => {
          addLog('Validation', 'info', log.message)
        })
        addLog(
          'Validation',
          'success',
          'Validation completed. Check server console for detailed counts.',
        )
        toast.success('Validation completed. Check console for details.')
      },
      onError: (error) => {
        const errorDetails = error.message || 'Unknown error occurred'
        const errorStack =
          'cause' in error &&
          typeof error.cause === 'object' &&
          error.cause &&
          'stack' in error.cause
            ? String(error.cause.stack)
            : undefined
        addLog('Validation', 'error', `Validation failed: ${errorDetails}`, errorStack)
        toast.error(`Validation failed: ${error.message}`)
      },
    }),
  })

  function readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        if (e.target?.result && typeof e.target.result === 'string') {
          resolve(e.target.result)
        } else {
          reject(new Error('Failed to read file'))
        }
      }
      reader.onerror = () => reject(new Error('Failed to read file'))
      reader.readAsText(file)
    })
  }

  return (
    <DashboardShell>
      <div className="flex flex-col gap-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">IE298 Seed Migration</h1>
          <p className="text-muted-foreground">Seed data for selected business organization</p>
        </div>

        {/* Business Selector */}
        <Card>
          <CardHeader>
            <CardTitle>Select Business</CardTitle>
            <CardDescription>
              Choose a business to seed data for. All operations below will use this business.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="business-select">Business</Label>
              <Select
                value={selectedBusinessId}
                onValueChange={(value) => {
                  // Only allow selection of allowed businesses
                  if (isBusinessAllowed(value)) {
                    setSelectedBusinessId(value)
                  }
                }}
                disabled={isLoadingOrganizations}
              >
                <SelectTrigger id="business-select" className="w-full">
                  <SelectValue placeholder="Select a business" />
                </SelectTrigger>
                <SelectContent>
                  {organizations?.map((org: { businessId: string; name: string }) => {
                    const isAllowed = isBusinessAllowed(org.businessId)
                    return (
                      <SelectItem
                        key={org.businessId}
                        value={org.businessId}
                        disabled={!isAllowed}
                        className={!isAllowed ? 'opacity-50 cursor-not-allowed' : ''}
                      >
                        {org.name}
                        {!isAllowed && ' (Not available for seeding)'}
                      </SelectItem>
                    )
                  })}
                </SelectContent>
              </Select>
              {selectedBusinessId && organizations && (
                <p className="text-sm text-muted-foreground">
                  Selected:{' '}
                  <span className="font-medium">
                    {organizations.find(
                      (o: { businessId: string; name: string }) =>
                        o.businessId === selectedBusinessId,
                    )?.name ?? 'Unknown'}
                  </span>
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800">
          <span className="font-medium">Warning:</span> These operations will modify the database.
          Use with caution. Each function can be run independently.
        </div>

        {/* Console Log Area */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Terminal className="size-5" />
                <CardTitle>Operation Logs</CardTitle>
              </div>
              {logs.length > 0 && (
                <Button variant="ghost" size="sm" onClick={clearLogs}>
                  <X className="size-4 mr-2" />
                  Clear Logs
                </Button>
              )}
            </div>
            <CardDescription>
              View operation results, success messages, and error details
            </CardDescription>
          </CardHeader>
          <CardContent>
            {logs.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Terminal className="size-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">
                  No operations logged yet. Run a seed operation to see results.
                </p>
              </div>
            ) : (
              <ScrollArea className="h-[400px] w-full rounded-md border bg-muted/50 p-4">
                <div className="space-y-3 font-mono text-sm">
                  {logs.map((log) => (
                    <div
                      key={log.id}
                      className={`rounded-md border p-3 ${
                        log.status === 'success'
                          ? 'bg-green-50 border-green-200 dark:bg-green-950 dark:border-green-800'
                          : log.status === 'error'
                            ? 'bg-red-50 border-red-200 dark:bg-red-950 dark:border-red-800'
                            : 'bg-blue-50 border-blue-200 dark:bg-blue-950 dark:border-blue-800'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="flex items-center gap-2">
                          <span
                            className={`font-semibold ${
                              log.status === 'success'
                                ? 'text-green-700 dark:text-green-300'
                                : log.status === 'error'
                                  ? 'text-red-700 dark:text-red-300'
                                  : 'text-blue-700 dark:text-blue-300'
                            }`}
                          >
                            [{log.status.toUpperCase()}]
                          </span>
                          <span className="font-medium text-foreground">{log.operation}</span>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {log.timestamp.toLocaleTimeString()}
                        </span>
                      </div>
                      <div
                        className={`text-sm ${
                          log.status === 'success'
                            ? 'text-green-800 dark:text-green-200'
                            : log.status === 'error'
                              ? 'text-red-800 dark:text-red-200'
                              : 'text-blue-800 dark:text-blue-200'
                        }`}
                      >
                        {log.message}
                      </div>
                      {log.details && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                            Show details
                          </summary>
                          <pre className="mt-2 text-xs overflow-auto bg-background/50 p-2 rounded border max-h-48">
                            {log.details}
                          </pre>
                        </details>
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            )}
          </CardContent>
        </Card>

        <div className="grid gap-4 md:grid-cols-2">
          {/* Calendar Categories */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="size-5" />
                Calendar Categories
              </CardTitle>
              <CardDescription>
                Create Regular Holiday, Special Holiday, and Regional Holiday categories
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => {
                  if (!selectedBusinessId) {
                    toast.error('Please select a business first')
                    return
                  }
                  addLog('Calendar Categories', 'info', 'Starting seed operation...')
                  seedCalendarCategories.mutate({ businessId: selectedBusinessId })
                }}
                disabled={seedCalendarCategories.isPending || !selectedBusinessId}
                className="w-full"
              >
                {seedCalendarCategories.isPending ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    Seeding...
                  </>
                ) : (
                  'Seed Calendar Categories'
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Holidays */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="size-5" />
                PH Holidays
              </CardTitle>
              <CardDescription>
                Create all Philippine holidays for the selected year
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="holiday-year">Year</Label>
                <Input
                  id="holiday-year"
                  type="number"
                  min="2000"
                  max="2100"
                  value={holidayYear}
                  onChange={(e) => {
                    const year = parseInt(e.target.value, 10)
                    if (!isNaN(year) && year >= 2000 && year <= 2100) {
                      setHolidayYear(year)
                    }
                  }}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  Select the year for which to seed Philippine holidays
                </p>
              </div>
              <Button
                onClick={() => {
                  if (!selectedBusinessId) {
                    toast.error('Please select a business first')
                    return
                  }
                  currentSeedingYearRef.current = holidayYear
                  addLog(
                    `PH Holidays ${holidayYear}`,
                    'info',
                    `Starting seed operation for ${holidayYear}...`,
                  )
                  seedHolidays.mutate({ businessId: selectedBusinessId, year: holidayYear })
                }}
                disabled={seedHolidays.isPending || !selectedBusinessId}
                className="w-full"
              >
                {seedHolidays.isPending ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    Seeding...
                  </>
                ) : (
                  `Seed Holidays for ${holidayYear}`
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Clients */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="size-5" />
                Clients
              </CardTitle>
              <CardDescription>
                Create 5 clients with varied settings (optional JSON file)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="client-data-file">Client Data (JSON, optional)</Label>
                <Input
                  id="client-data-file"
                  type="file"
                  accept=".json"
                  onChange={(e) => {
                    const file = e.target.files?.[0] ?? null
                    setClientDataFile(file)
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  Leave empty to use default client data
                </p>
              </div>
              <Button
                onClick={async () => {
                  if (!selectedBusinessId) {
                    toast.error('Please select a business first')
                    return
                  }
                  addLog('Clients', 'info', 'Starting seed operation...')
                  seedClients.mutate({
                    businessId: selectedBusinessId,
                    clientDataJson: clientDataFile
                      ? await readFileAsText(clientDataFile)
                      : undefined,
                  })
                }}
                disabled={seedClients.isPending || !selectedBusinessId}
                className="w-full"
              >
                {seedClients.isPending ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    Seeding...
                  </>
                ) : (
                  'Seed Clients'
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Users and Employees */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="size-5" />
                Users & Employees
              </CardTitle>
              <CardDescription>
                Create 25 users with matching employee profiles (optional JSON file)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="employee-data-file">Employee Data (JSON, optional)</Label>
                <Input
                  id="employee-data-file"
                  type="file"
                  accept=".json"
                  onChange={(e) => {
                    const file = e.target.files?.[0] ?? null
                    setEmployeeDataFile(file)
                  }}
                />
                <p className="text-xs text-muted-foreground">
                  Leave empty to use default employee data
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name-type-select">Name Type</Label>
                <Select
                  value={nameType}
                  onValueChange={(value: 'fictional' | 'corporate') => setNameType(value)}
                >
                  <SelectTrigger id="name-type-select" className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fictional">Fictional</SelectItem>
                    <SelectItem value="corporate">Corporate</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {nameType === 'fictional'
                    ? 'Uses names from JSON file (fictional characters)'
                    : 'Generates corporate names from predefined lists'}
                </p>
              </div>

              <Button
                onClick={async () => {
                  if (!selectedBusinessId) {
                    toast.error('Please select a business')
                    return
                  }

                  addLog(
                    'Users & Employees',
                    'info',
                    `Starting seed operation for 25 users and employees (${nameType} names)...`,
                  )
                  seedUsersAndEmployees.mutate({
                    businessId: selectedBusinessId,
                    employeeDataJson: employeeDataFile
                      ? await readFileAsText(employeeDataFile)
                      : undefined,
                    nameType,
                  })
                }}
                disabled={seedUsersAndEmployees.isPending || !selectedBusinessId}
                className="w-full"
              >
                {seedUsersAndEmployees.isPending ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    Seeding...
                  </>
                ) : (
                  'Seed Users & Employees'
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Client Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="size-5" />
                Client Settings
              </CardTitle>
              <CardDescription>
                Create settings for all clients (shift times, multipliers, etc.)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => {
                  if (!selectedBusinessId) {
                    toast.error('Please select a business first')
                    return
                  }
                  addLog('Client Settings', 'info', 'Starting seed operation...')
                  seedClientSettings.mutate({ businessId: selectedBusinessId })
                }}
                disabled={seedClientSettings.isPending || !selectedBusinessId}
                className="w-full"
              >
                {seedClientSettings.isPending ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    Seeding...
                  </>
                ) : (
                  'Seed Client Settings'
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Employee Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="size-5" />
                Employee Settings
              </CardTitle>
              <CardDescription>
                Create settings for all employees (pay rates, overrides, etc.)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => {
                  if (!selectedBusinessId) {
                    toast.error('Please select a business first')
                    return
                  }
                  addLog('Employee Settings', 'info', 'Starting seed operation...')
                  seedEmployeeSettings.mutate({ businessId: selectedBusinessId })
                }}
                disabled={seedEmployeeSettings.isPending || !selectedBusinessId}
                className="w-full"
              >
                {seedEmployeeSettings.isPending ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    Seeding...
                  </>
                ) : (
                  'Seed Employee Settings'
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Government Contribution Brackets */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="size-5" />
                Government Contribution Brackets
              </CardTitle>
              <CardDescription>
                Create SSS, HDMF, PhilHealth, and Tax contribution brackets
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => {
                  if (!selectedBusinessId) {
                    toast.error('Please select a business first')
                    return
                  }
                  addLog(
                    'Government Contribution Brackets',
                    'info',
                    'Starting seed operation for contribution brackets...',
                  )
                  seedGovernmentContributionBrackets.mutate({ businessId: selectedBusinessId })
                }}
                disabled={seedGovernmentContributionBrackets.isPending || !selectedBusinessId}
                className="w-full"
              >
                {seedGovernmentContributionBrackets.isPending ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    Seeding...
                  </>
                ) : (
                  'Seed Contribution Brackets'
                )}
              </Button>
            </CardContent>
          </Card>

          {/* <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="size-5" />
                Government Contributions
              </CardTitle>
              <CardDescription>
                Create SSS, PhilHealth, and HDMF records for all employees
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => {
                  addLog(
                    'Government Contributions',
                    'info',
                    'Starting seed operation for SSS, PhilHealth, and HDMF...',
                  )
                  seedGovernmentContributions.mutate(undefined)
                }}
                disabled={seedGovernmentContributions.isPending}
                className="w-full"
              >
                {seedGovernmentContributions.isPending ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    Seeding...
                  </>
                ) : (
                  'Seed Government Contributions'
                )}
              </Button>
            </CardContent>
          </Card> */}

          {/* Timesheets */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="size-5" />
                Timesheets
              </CardTitle>
              <CardDescription>
                Create timesheets from March 1 to November 22, 2025 (Philippine time)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => {
                  if (!selectedBusinessId) {
                    toast.error('Please select a business first')
                    return
                  }
                  addLog('Timesheets', 'info', 'Starting seed operation for timesheets...')
                  seedTimesheets.mutate({ businessId: selectedBusinessId })
                }}
                disabled={seedTimesheets.isPending || !selectedBusinessId}
                className="w-full"
              >
                {seedTimesheets.isPending ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    Seeding...
                  </>
                ) : (
                  'Seed Timesheets'
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Validate */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="size-5" />
                Validate Seeded Data
              </CardTitle>
              <CardDescription>
                Check and count all seeded data (check console for output)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => {
                  if (!selectedBusinessId) {
                    toast.error('Please select a business first')
                    return
                  }
                  addLog('Validation', 'info', 'Starting validation of seeded data...')
                  validateSeededData.mutate({ businessId: selectedBusinessId })
                }}
                disabled={validateSeededData.isPending || !selectedBusinessId}
                variant="outline"
                className="w-full"
              >
                {validateSeededData.isPending ? (
                  <>
                    <Loader2 className="mr-2 size-4 animate-spin" />
                    Validating...
                  </>
                ) : (
                  'Validate Data'
                )}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardShell>
  )
}
